/**
 * Lesson Submission Service
 *
 * Submit flow:
 *   1. Get or create the OriginalVideo document
 *   2. Save the draft (upload blobs to storage)
 *   3. Call publishLessonDraft CF — fast server-side delivery (< 2s):
 *        publish draft → mark delivered → start video composition job →
 *        notify player → check order completion
 *        The video-compose Cloud Run Job builds the final MP4 from the published
 *        draft and writes videoProcessingStatus → 'processing' | 'complete' | 'failed'.
 *        Nothing about composition runs in the browser, so a closed tab or a busy
 *        Cloud Run service cannot lose it.
 *
 * Used by both LessonDetailPanel and ConversationView.
 */

import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../firebase';
import type { User } from 'firebase/auth';
import type { VideoEditorDraftData } from '../components/LessonVideoEditor';
import type { Lesson, LessonDrillRef } from '../types/lesson';
import type { OriginalVideo, VideoEditDraft, VideoContext } from '../types/videoEdit';
import {
  getOriginalVideo,
  createOriginalVideo,
  saveVideoEditDraft,
} from './videoEditDraft';

// ============================================
// Shared: get or create the OriginalVideo
// ============================================

export async function getOrCreateOriginalVideo(
  lesson: Lesson,
  lessonContext: VideoContext,
): Promise<OriginalVideo> {
  const videoId = `lesson_${lesson.id}`;

  let video: OriginalVideo | null = null;
  try {
    video = await getOriginalVideo(lessonContext, videoId);
  } catch {
    // Security rule get() can throw under emulator load — fall through to create
  }
  if (!video && lesson.playerVideoStoragePath) {
    video = await createOriginalVideo(
      lessonContext,
      lesson.playerId,
      lesson.playerId,
      lesson.playerVideoStoragePath,
      0,
      undefined,
      `Player's Video`,
      undefined,
      videoId,
    );
  }

  if (!video) {
    throw new Error('Could not create video document');
  }

  return video;
}

// ============================================
// Process, publish and complete a saved draft
// ============================================

interface PublishSavedDraftParams {
  user: User;
  lesson: Lesson;
  lessonContext: VideoContext;
  originalVideo: OriginalVideo;
  savedDraft: VideoEditDraft;
  notes?: string;
  attachedDrills?: LessonDrillRef[];
  onStatusUpdate?: (message: string) => void;
}

/**
 * Delivers the lesson via the publishLessonDraft CF (fast, server-side), which also
 * starts video composition as a Cloud Run Job. Returns as soon as the lesson is marked
 * delivered — video composition continues independently.
 */
export async function publishAndCompleteDraft(
  params: PublishSavedDraftParams,
): Promise<void> {
  const { lesson, originalVideo, savedDraft, notes, attachedDrills, onStatusUpdate } =
    params;

  onStatusUpdate?.('Delivering lesson...');

  // Deliver the lesson synchronously via the CF. This is the authoritative delivery
  // step: publish draft, mark delivered, send message, check order completion.
  const deliverFn = httpsCallable(functions, 'publishLessonDraft');
  await deliverFn({
    lessonId: lesson.id,
    originalVideoId: originalVideo.id,
    draftId: savedDraft.id,
    coachNotes: notes ?? savedDraft.notes,
    attachedDrills: attachedDrills as Record<string, unknown>[] | undefined,
  });
}

// ============================================
// Full submit flow (save + process + publish + complete)
// ============================================

interface SubmitLessonDraftParams {
  user: User;
  lesson: Lesson;
  data: VideoEditorDraftData;
  /** Pass existing original video if already loaded, avoids extra Firestore read */
  existingOriginalVideo?: OriginalVideo | null;
  existingDraftId?: string;
  onStatusUpdate?: (message: string) => void;
}

interface SubmitLessonDraftResult {
  originalVideo: OriginalVideo;
  savedDraftId: string;
}

/**
 * Full lesson submission pipeline:
 *   save draft → deliver lesson → kick off background video composition
 *
 * Called from the LessonVideoEditor's onSubmitDraft callback.
 */
export async function submitLessonDraft(
  params: SubmitLessonDraftParams,
): Promise<SubmitLessonDraftResult> {
  const { user, lesson, data, existingOriginalVideo, existingDraftId, onStatusUpdate } =
    params;

  const lessonContext: VideoContext = { type: 'lesson', id: lesson.id };
  const lessonRef = doc(db, 'lessons', lesson.id);

  /* 0. Record the intent to submit, before anything slow or losable happens.
        Awaited on purpose: if we cannot even record that a submission started
        then we should fail here, loudly, rather than begin a half-hour upload
        that nothing is tracking. It is one small write against a document the
        coach already has permission to update. */
  await updateDoc(lessonRef, {
    submissionStatus: 'submitting',
    submissionStartedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  try {
    // 1. Get or create the OriginalVideo
    let video: OriginalVideo;
    if (existingOriginalVideo) {
      video = existingOriginalVideo;
    } else {
      video = await getOrCreateOriginalVideo(lesson, lessonContext);
    }

    // 2. Save the draft (uploads blobs to storage)
    const savedDraft = await saveVideoEditDraft({
      userId: user.uid,
      userName: user.displayName || 'Coach',
      context: lessonContext,
      originalVideoId: video.id,
      audioMuted: data.audioMuted,
      notes: data.notes,
      existingDraftId,
      notableSegments: data.notableSegments,
      voiceoverNotableSegmentClips: data.voiceoverNotableSegmentClips,
      coachNotes: data.coachNotes,
      attachedDrills: data.attachedDrills,
      videoRotation: data.videoRotation,
    });

    // 3. Deliver lesson + kick off background video composition
    await publishAndCompleteDraft({
      user,
      lesson,
      lessonContext,
      originalVideo: video,
      savedDraft,
      notes: data.notes,
      attachedDrills: data.attachedDrills,
      onStatusUpdate,
    });

    /* 4. Close the intent out. publishLessonDraft has returned by now, so the
          lesson really is delivered; this only records that the client saw it
          happen. Deliberately not merged into publishAndCompleteDraft, which
          the brief says not to change. */
    await updateDoc(lessonRef, {
      submissionStatus: 'delivered',
      updatedAt: serverTimestamp(),
    });

    return { originalVideo: video, savedDraftId: savedDraft.id };
  } catch (err) {
    /* Mark the attempt failed so it is recoverable, then rethrow so the
       caller's existing .catch still fires its UI event. The write is itself
       guarded: if the failure was the network dying, this cannot succeed
       either, and swallowing that must not mask the original error. A lesson
       left in 'submitting' because even this could not be written is still
       visible, which is the entire point of writing it up front. */
    await updateDoc(lessonRef, {
      submissionStatus: 'failed',
      submissionError: err instanceof Error ? err.message : String(err),
      updatedAt: serverTimestamp(),
    }).catch(() => { /* original error wins */ });
    throw err;
  }
}
